import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ReportsIcon from "@mui/icons-material/Unarchive";
import Divider from "@mui/material/Divider";
import AssignPrescriptionIcon from "@mui/icons-material/NoteAdd";
import InventoryIcon from "@mui/icons-material/Inventory2";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PaymentsIcon from "@mui/icons-material/Payments";
import GroupsIcon from "@mui/icons-material/Groups";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import StorefrontIcon from "@mui/icons-material/Storefront";
import GavelIcon from "@mui/icons-material/Gavel";
import HubIcon from "@mui/icons-material/Hub";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import UndoIcon from "@mui/icons-material/Undo";
import ViewListIcon from "@mui/icons-material/ViewList";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PaymentIcon from "@mui/icons-material/Payment";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import PeopleIcon from "@mui/icons-material/People";
import LoyaltyIcon from "@mui/icons-material/Loyalty";
import PollIcon from "@mui/icons-material/Poll";
import ForumIcon from "@mui/icons-material/Forum";
import EventIcon from "@mui/icons-material/Event";
import MedicationIcon from "@mui/icons-material/Medication";
import LoopIcon from "@mui/icons-material/Loop";
import TerminalIcon from "@mui/icons-material/Terminal";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import LockIcon from "@mui/icons-material/Lock";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import SmsIcon from "@mui/icons-material/Sms";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import HistoryIcon from "@mui/icons-material/History";
import { Link, Location } from "@reach/router";
import USER_ROLES from "../common/userroles";
import { withTranslation } from "react-i18next";
import connectionType from "../common/connection-type";
import { navActiveStyles, isNavPathActive } from "./navActiveStyles";
import { hasAllowedPermission, isStockerNavGated, syncStockerUserFlag } from "../../utils/stockerNavPermissions";
import NavMiniFlyout from "./NavMiniFlyout";
import NavCollapsedTitle from "./NavCollapsedTitle";
import QueueNavItems from "../workflowQueue/QueueNavItems";
import { isDeliverPrescriptionPath } from "../../utils/productLicensing";
import { isNewUiHidden } from "../../utils/hideNewUi";
import {
  TOP_LEVEL_NAV,
  isTopLevelNavSectionOpen,
  toggleTopLevelNavSection,
  subscribeTopLevelNavAccordion,
} from "./navAccordion";

const MARKETPLACE_REPORT_PATHS = [
  "/home/reports/feature-purchases",
  "/home/reports/feature-requests",
  "/home/reports/feature-enablement-history",
];

export { MARKETPLACE_REPORT_PATHS };

export const isMarketplaceReportPath = (pathname) =>
  MARKETPLACE_REPORT_PATHS.some((reportPath) =>
    isNavPathActive(pathname, reportPath)
  );

export const isGeneralReportPath = (pathname) =>
  (isNavPathActive(pathname, "/home/reports", { prefix: true }) ||
    String(pathname || "").includes("/home/reports/")) &&
  !isMarketplaceReportPath(pathname);

const NAV_MENU_MAX_WIDTH = Math.round(360 * 1.15);

const styles = (theme) => ({
  root: {
    width: "100%",
    maxWidth: NAV_MENU_MAX_WIDTH,
    backgroundColor: theme.palette.background.paper,
    ...navActiveStyles,
    '& a[aria-current="page"] .deliver-prescription-nav': {
      backgroundColor: "#0a5d66 !important",
    },
    '& a[aria-current="page"] .deliver-prescription-nav .MuiListItemText-primary': {
      color: "#ffffff !important",
      fontWeight: 700,
    },
    '& a[aria-current="page"] .deliver-prescription-nav .MuiListItemIcon-root': {
      color: "#ffffff !important",
    },
    "& .MuiListItemButton-root.deliver-prescription-nav.Mui-selected": {
      backgroundColor: "#0a5d66 !important",
    },
    "& .MuiListItemButton-root.deliver-prescription-nav.Mui-selected .MuiListItemText-primary": {
      color: "#ffffff !important",
      fontWeight: 700,
    },
    "& .MuiListItemButton-root.deliver-prescription-nav.Mui-selected .MuiListItemIcon-root": {
      color: "#ffffff !important",
    },
  },
  textColor: {
    color: "#334155",
  },
  listItem: {
    "&.MuiButtonBase-root": {
      display: "flex",
      minHeight: 32,
      padding: "4px 24px",
      marginBottom: 2,
    },
  },
  primaryNavItem: {
    "&.MuiButtonBase-root": {
      padding: "4px 24px",
      minHeight: 32,
      marginBottom: 2,
    },
  },
  primaryNavDivider: {
    margin: "2px 0",
  },
  navLink: {
    textDecoration: "none",
    display: "block",
    color: "inherit",
  },
  deliverNavWrap: {
    margin: "2px 10px",
  },
  deliverNavItem: {
    "&.MuiButtonBase-root": {
      padding: "4px 14px",
      minHeight: 36,
      borderRadius: 10,
      backgroundColor: "#1197B2 !important",
      color: "#ffffff",
      boxShadow: "0 1px 3px rgba(15, 23, 42, 0.2)",
      "&:hover": {
        backgroundColor: "#0e7c94 !important",
      },
    },
    "& .MuiListItemIcon-root": {
      minWidth: 28,
      color: "#ffffff !important",
    },
    "& .MuiListItemText-primary": {
      color: "#ffffff !important",
      fontWeight: 700,
      letterSpacing: "0.01em",
      fontSize: "0.8rem !important",
    },
  },
  deliverNavText: {
    color: "#ffffff",
  },
  nestedReportsToggle: {
    paddingLeft: "48px !important",
  },
  nestedReportsMenu: {
    paddingLeft: 0,
  },
  nestedReportsMenuItem: {
    paddingLeft: "64px !important",
  },
  reportCategoryToggle: {
    paddingLeft: "24px !important",
  },
  reportCategoryMenu: {
    paddingLeft: 0,
  },
  reportCategoryMenuItem: {
    paddingLeft: "48px !important",
  },
  reportCategoryLabel: {
    fontSize: "0.8rem !important",
    fontWeight: 500,
  },
  reportCategoryRibbonLabel: {
    color: "#334155",
  },
  nested: {
    "&.MuiButtonBase-root": {
      paddingLeft: "48px !important",
    },
  },
  navItemIcon: {
    minWidth: 28,
    color: "#475569",
  },
});

class NestedList extends React.Component {
  state = {
    reportCategoriesOpen: {},
    Dashboard: false,
    // assignprescription: true,
    reports: true,
    assigneditemsreports: true,
    stockeditemsreports: true,
    cancelledItemsReports: true,
    pickedUpitemsreports: true,
    controlledSubstanceDispensedReport: true,
    transactionsreports: true,
    enrolledPatientsreports: true,
    DeliveryTech: false,
    isIpayTransactionAllowed: true,
    isIpayReceivablesAllowed: true,
    isTemperatureMonitoringAllowed: true,
    isReturnToStockAllowed: true,
    isSurveyReportAllowed: true,
    isConsultationReportAllowed: true,
    isScheduleConsultationAllowed: true,
    isOutForDeliveryAllowed: true,
    isDeliveredItemsAllowed: true,
    isLoyaltiMembersAllowed: true,
    isOrderStagingViewAllowed: true,
    isConsoleReportAllowed: true,
    payrollDeductionsreports: true,
    inActiveUsresAllowed: true,
    kioskUtilizationAllowed: true,
    isPhiLogsVisible: false,
    isPurgeTrailVisible: false,
    isCycleReview: false,
    isAuditTrailReportAllowed: false,
    isDeliverPrescriptionVisible: false,
    isHL7OrdersViewVisible: false,
    isComplianceReconciliation: false,
    isMessageLogVisible: true,
    isCashReceiptsAllowed: true,
  };
  handleReportsMenuToggle = () => {
    toggleTopLevelNavSection(TOP_LEVEL_NAV.REPORTS);
  };
  handleCategoryClick = (categoryId) => {
    const currentlyOpen = this.getCategoryOpen(categoryId);
    if (currentlyOpen) {
      this.setState({ reportCategoriesOpen: {} });
      return;
    }
    this.setState({
      reportCategoriesOpen: {
        [categoryId]: true,
      },
    });
  };
  getCategoryOpen = (categoryId) => {
    if (
      Object.prototype.hasOwnProperty.call(
        this.state.reportCategoriesOpen,
        categoryId
      )
    ) {
      return this.state.reportCategoriesOpen[categoryId];
    }
    return false;
  };
  getReportCategoryRibbonClass = (categoryId) =>
    "report-cat-ribbon report-cat-" + categoryId;
  isCategoryActive = (pathname, paths) =>
    paths.some((reportPath) =>
      isNavPathActive(pathname, reportPath, { prefix: true })
    );
  wrapNavTitle = (title, node) => (
    <NavCollapsedTitle show={!this.props.drawerOpen} title={title}>
      {node}
    </NavCollapsedTitle>
  );
  renderDeliverPrescriptionNav = (location) => {
    const { classes, t } = this.props;
    if (!this.state.isDeliverPrescriptionVisible || this.state.DeliveryTech) {
      return null;
    }
    const deliverPath =
      this.props.isVisible === true
        ? "/home/prescription/unpluggedassign"
        : "/home/prescription/assign";
    return (
      <div className={classes.deliverNavWrap} data-testid="nav-deliver-prescription">
        {this.wrapNavTitle(
          t("Leftbar.DeliverPrescription"),
          <Link to={deliverPath} className={classes.navLink}>
            <ListItemButton
              className={`${classes.listItem} ${classes.deliverNavItem} deliver-prescription-nav`}
              selected={isDeliverPrescriptionPath(location.pathname)}
            >
              <ListItemIcon>
                <AssignPrescriptionIcon />
              </ListItemIcon>
              <ListItemText
                primary={t("Leftbar.DeliverPrescription")}
                className={classes.deliverNavText}
              />
            </ListItemButton>
          </Link>
        )}
      </div>
    );
  };
  renderNavIcon = (IconComponent) => {
    if (!IconComponent) {
      return null;
    }
    const { classes } = this.props;
    return (
      <ListItemIcon className={classes.navItemIcon}>
        <IconComponent fontSize="small" />
      </ListItemIcon>
    );
  };
  renderReportNavLink = (to, label, location, nested, matchPath, IconComponent) => {
    const { classes } = this.props;
    const activePath = matchPath || to;
    const listItemClass = nested
      ? `${classes.listItem} ${classes.reportCategoryMenuItem} ${classes.nestedReportsMenuItem}`
      : `${classes.listItem} ${classes.reportCategoryMenuItem}`;
    return (
      <Link to={to} className={classes.navLink} key={to}>
        <List component="div" disablePadding>
          <ListItemButton
            className={listItemClass}
            selected={isNavPathActive(location.pathname, activePath)}
          >
            {this.renderNavIcon(IconComponent)}
            <ListItemText
              primary={label}
              className={classes.textColor}
              style={{ paddingLeft: "0px" }}
            />
          </ListItemButton>
        </List>
      </Link>
    );
  };
  renderReportCategory = (
    categoryId,
    label,
    location,
    nested,
    paths,
    items,
    IconComponent,
    flyoutMode
  ) => {
    const { classes } = this.props;
    const visibleItems = items.filter(Boolean);
    if (!visibleItems.length) {
      return null;
    }
    const categoryActive = this.isCategoryActive(location.pathname, paths);
    const categoryOpen = this.getCategoryOpen(categoryId);
    let toggleClass =
      `${classes.listItem} ${classes.reportCategoryToggle} ` +
      this.getReportCategoryRibbonClass(categoryId);
    if (nested) {
      toggleClass += ` ${classes.nestedReportsToggle}`;
    }
    if (flyoutMode) {
      toggleClass += " nav-flyout-section-toggle";
    }
    const menuClass = flyoutMode
      ? "nav-flyout-section-menu"
      : nested
        ? classes.nestedReportsMenu
        : classes.reportCategoryMenu;
    return (
      <React.Fragment key={categoryId}>
        <ListItemButton
          onClick={() => this.handleCategoryClick(categoryId)}
          className={toggleClass}
          selected={categoryActive}
        >
          {this.renderNavIcon(IconComponent)}
          <ListItemText
            primary={label}
            className={`${classes.reportCategoryLabel} ${classes.reportCategoryRibbonLabel}`}
            style={{ paddingLeft: "0px" }}
          />
          {categoryOpen ? (
            <ExpandLess className="nav-section-chevron" />
          ) : (
            <ExpandMore className="nav-section-chevron" />
          )}
        </ListItemButton>
        <Collapse in={categoryOpen} timeout="auto" unmountOnExit>
          <div className={menuClass}>{visibleItems}</div>
        </Collapse>
      </React.Fragment>
    );
  };
  renderCategorizedReports = (location, nested, permissions, flyoutMode) => {
    const { t } = this.props;
    const {
      payrollDeductionsreports,
      isReturnToStockAllowed,
      isSurveyReportAllowed,
      isConsultationReportAllowed,
      isScheduleConsultationAllowed,
      isOutForDeliveryAllowed,
      isDeliveredItemsAllowed,
      isIpayTransactionAllowed,
      isIpayReceivablesAllowed,
      isCashReceiptsAllowed,
      isTemperatureMonitoringAllowed,
      isLoyaltiMembersAllowed,
      isOrderStagingViewAllowed,
      isConsoleReportAllowed,
      inActiveUsresAllowed,
      kioskUtilizationAllowed,
      isPhiLogsVisible,
      isPurgeTrailVisible,
      isCycleReview,
      isAuditTrailReportAllowed,
      isHL7OrdersViewVisible,
      isComplianceReconciliation,
      isMessageLogVisible,
      assigneditemsreports,
      stockeditemsreports,
      pickedUpitemsreports,
      controlledSubstanceDispensedReport,
      cancelledItemsReports,
      transactionsreports,
      enrolledPatientsreports,
    } = permissions;
    const accountConnectionId = sessionStorage.getItem("accountConnectionId");
    const link = (to, labelKey, IconComponent, matchPath) =>
      this.renderReportNavLink(
        to,
        t(labelKey),
        location,
        nested,
        matchPath,
        IconComponent
      );
    const inventoryPaths = [
      "/home/reports/assigned-items",
      "/home/reports/stock-list",
      "/home/reports/pickup",
      "/home/reports/cancelled-list",
      "/home/reports/transaction/TransactionLists",
      "/home/reports/return-to-stock",
      "/home/reports/orders-view",
      "/home/reports/vendor-orders",
    ];
    const deliveryPaths = [
      "/home/reports/out-of-delivery",
      "/home/reports/my-delivery",
    ];
    const paymentsPaths = [
      "/home/reports/payroll-deductions",
      "/home/reports/transaction/ipaytransactions",
      "/home/reports/transaction/ipayreceivables",
      "/home/reports/transaction/cashreceipts",
    ];
    const patientsPaths = [
      "/home/reports/enrolled-patients",
      "/home/reports/enrolled-loyalty-members",
      "/home/reports/survey-report",
    ];
    const clinicalPaths = [
      "/home/reports/consultation",
      "/home/reports/controlled-substance-dispensed",
      "/home/reports/cycle-report",
    ];
    const operationsPaths = [
      "/home/reports/console-report",
      "/home/reports/kiosk-utilization",
      "/home/reports/temperaturemonitoring",
    ];
    const compliancePaths = [
      "/home/reports/audit-trail",
      "/home/reports/purge-trail-report",
      "/home/reports/ComplianceReconciliationList",
      "/home/reports/phi-logs",
    ];
    const systemPaths = [
      "/home/reports/hl7-orders",
      "/home/reports/message-log",
      "/home/reports/inactive-users-report",
    ];
    return (
      <React.Fragment>
        {this.renderReportCategory(
          "inventory",
          t("Leftbar.ReportCategory.Inventory", {
            defaultValue: "Orders & Inventory",
          }),
          location,
          nested,
          inventoryPaths,
          [
            assigneditemsreports === true
              ? link(
                  "/home/reports/assigned-items/assigneditems",
                  "Leftbar.AssignedItemReport",
                  AssignmentIcon
                )
              : null,
            stockeditemsreports === true
              ? link(
                  "/home/reports/stock-list/StockLists",
                  "Leftbar.StockedItemReport",
                  InventoryIcon
                )
              : null,
            pickedUpitemsreports === true
              ? link(
                  "/home/reports/pickup/PickUpLists",
                  "Leftbar.PickupItemReport",
                  CheckCircleIcon
                )
              : null,
            transactionsreports === true
              ? link(
                  "/home/reports/transaction/TransactionLists",
                  "Leftbar.TransactionsReport",
                  ReceiptLongIcon
                )
              : null,
            cancelledItemsReports === true
              ? link(
                  "/home/reports/cancelled-list/CancelledLists",
                  "Leftbar.CancelledItemsReport",
                  CancelIcon
                )
              : null,
            isReturnToStockAllowed
              ? link(
                  "/home/reports/return-to-stock/items",
                  "Leftbar.RetrunToStockItemsReport",
                  UndoIcon
                )
              : null,
            isOrderStagingViewAllowed
              ? link(
                  "/home/reports/orders-view",
                  "Leftbar.OrderStagingReport",
                  ViewListIcon
                )
              : null,
            accountConnectionId === connectionType.THIRD_PARTY && !isStockerNavGated()
              ? link(
                  "/home/reports/vendor-orders",
                  "Leftbar.VendorOrdersReport",
                  StorefrontIcon
                )
              : null,
          ],
          InventoryIcon,
          flyoutMode
        )}
        {this.renderReportCategory(
          "delivery",
          t("Leftbar.ReportCategory.Delivery", {
            defaultValue: "Delivery",
          }),
          location,
          nested,
          deliveryPaths,
          [
            isOutForDeliveryAllowed
              ? link(
                  "/home/reports/out-of-delivery/delivery",
                  "Leftbar.OutForDeliveryReport",
                  LocalShippingIcon
                )
              : null,
            isDeliveredItemsAllowed
              ? link(
                  "/home/reports/my-delivery/delivery",
                  "Leftbar.DeliveredItemReport",
                  DoneAllIcon
                )
              : null,
          ],
          LocalShippingIcon,
          flyoutMode
        )}
        {this.renderReportCategory(
          "payments",
          t("Leftbar.ReportCategory.Payments", {
            defaultValue: "Payments & Transactions",
          }),
          location,
          nested,
          paymentsPaths,
          [
            payrollDeductionsreports
              ? link(
                  "/home/reports/payroll-deductions/payrollDeductions",
                  "Leftbar.PayrollDeductionReport",
                  AccountBalanceWalletIcon
                )
              : null,
            isIpayTransactionAllowed
              ? link(
                  "/home/reports/transaction/ipaytransactions",
                  "Leftbar.iPayReport",
                  PaymentIcon
                )
              : null,
            isIpayReceivablesAllowed
              ? link(
                  "/home/reports/transaction/ipayreceivables",
                  "Leftbar.iPayReceivableReport",
                  RequestQuoteIcon
                )
              : null,
            isCashReceiptsAllowed
              ? link(
                  "/home/reports/transaction/cashreceipts",
                  "Leftbar.CashReceiptsReport",
                  ReceiptLongIcon
                )
              : null,
          ],
          PaymentsIcon,
          flyoutMode
        )}
        {this.renderReportCategory(
          "patients",
          t("Leftbar.ReportCategory.Patients", {
            defaultValue: "Patients & Engagement",
          }),
          location,
          nested,
          patientsPaths,
          [
            enrolledPatientsreports === true
              ? link(
                  "/home/reports/enrolled-patients/enrolledpatients",
                  "Leftbar.PatientListReport",
                  PeopleIcon
                )
              : null,
            isLoyaltiMembersAllowed
              ? link(
                  "/home/reports/enrolled-loyalty-members",
                  "Leftbar.LoyaltyReport",
                  LoyaltyIcon
                )
              : null,
            isSurveyReportAllowed
              ? link(
                  "/home/reports/survey-report/surveyreport",
                  "Leftbar.SurveyItemReport",
                  PollIcon
                )
              : null,
          ],
          GroupsIcon,
          flyoutMode
        )}
        {this.renderReportCategory(
          "clinical",
          t("Leftbar.ReportCategory.Clinical", {
            defaultValue: "Clinical & Consultation",
          }),
          location,
          nested,
          clinicalPaths,
          [
            isConsultationReportAllowed
              ? link(
                  "/home/reports/consultation/consultreport",
                  "Leftbar.ConsultationReport",
                  ForumIcon
                )
              : null,
            isScheduleConsultationAllowed
              ? link(
                  "/home/reports/consultation/scheduleconsultreport",
                  "Leftbar.ScheduleReport",
                  EventIcon
                )
              : null,
            controlledSubstanceDispensedReport === true
              ? link(
                  "/home/reports/controlled-substance-dispensed",
                  "Leftbar.ControlledSubstanceDispensed",
                  MedicationIcon
                )
              : null,
            isCycleReview
              ? link("/home/reports/cycle-report", "Report.CycleReview", LoopIcon)
              : null,
          ],
          MedicalServicesIcon,
          flyoutMode
        )}
        {this.renderReportCategory(
          "operations",
          t("Leftbar.ReportCategory.Operations", {
            defaultValue: "Operations & Kiosk",
          }),
          location,
          nested,
          operationsPaths,
          [
            isConsoleReportAllowed
              ? link(
                  "/home/reports/console-report",
                  "Leftbar.ConsoleReport",
                  TerminalIcon
                )
              : null,
            kioskUtilizationAllowed
              ? link(
                  "/home/reports/kiosk-utilization",
                  "Leftbar.KioskUtilReport",
                  AnalyticsIcon
                )
              : null,
            isTemperatureMonitoringAllowed
              ? link(
                  "/home/reports/temperaturemonitoring",
                  "Leftbar.TemperatureReport",
                  ThermostatIcon
                )
              : null,
          ],
          StorefrontIcon,
          flyoutMode
        )}
        {this.renderReportCategory(
          "compliance",
          t("Leftbar.ReportCategory.Compliance", {
            defaultValue: "Compliance & Audit",
          }),
          location,
          nested,
          compliancePaths,
          [
            isAuditTrailReportAllowed
              ? link(
                  "/home/reports/audit-trail",
                  "Leftbar.AuditReport",
                  ManageSearchIcon
                )
              : null,
            isPurgeTrailVisible
              ? link(
                  "/home/reports/purge-trail-report",
                  "Report.purgeTrail.ReportTitle",
                  DeleteSweepIcon
                )
              : null,
            isComplianceReconciliation
              ? link(
                  "/home/reports/ComplianceReconciliationList",
                  "Report.ComplianceReconciliation",
                  FactCheckIcon
                )
              : null,
            isPhiLogsVisible
              ? link("/home/reports/phi-logs", "Leftbar.PhiLogs", LockIcon)
              : null,
          ],
          GavelIcon,
          flyoutMode
        )}
        {this.renderReportCategory(
          "system",
          t("Leftbar.ReportCategory.System", {
            defaultValue: "System & Integrations",
          }),
          location,
          nested,
          systemPaths,
          [
            isHL7OrdersViewVisible
              ? link(
                  "/home/reports/hl7-orders",
                  "Leftbar.HLOrderStagingReport",
                  IntegrationInstructionsIcon
                )
              : null,
            isMessageLogVisible
              ? link(
                  "/home/reports/message-log",
                  "Report.MessageLog.MessageLogTitle",
                  SmsIcon
                )
              : null,
            inActiveUsresAllowed
              ? link(
                  "/home/reports/inactive-users-report",
                  "Leftbar.InactiveUserReport",
                  PersonOffIcon
                )
              : null,
          ],
          HubIcon,
          flyoutMode
        )}
      </React.Fragment>
    );
  };

  // Route highlight must not force Collapse open — that blocked caret close on report pages.
  // Auto-expand only when entering /home/reports; manual toggle owns open afterward.
  _wasOnReports = false;

  syncReportsOpenFromRoute = () => {
    const onReports = !!this._currentOnReports;
    if (onReports && !this._wasOnReports && !this.state.open) {
      this.setState({ open: true });
    }
    this._wasOnReports = onReports;
  };

  componentDidUpdate() {
    this.syncReportsOpenFromRoute();
  }

  componentDidMount() {
    this._navAccordionUnsub = subscribeTopLevelNavAccordion(() => {
      this.forceUpdate();
    });
    syncStockerUserFlag();
    let permissions = JSON.parse(sessionStorage.getItem("permissions"));
    let _checkUserRole = JSON.parse(sessionStorage.getItem("superadmin"));
    const stockerGated = isStockerNavGated();
    // let _isRetailLocal = localStorage.getItem("isRetail");
    // let _isRetail;
    // if (![undefined, undefined, null, null, ""].includes(_isRetailLocal)) {
    //   _isRetail = [true, "true"].includes(_isRetailLocal) ? true : false;
    // } else {
    //   _isRetail = false;
    // }
    // if (_checkUserRole !== USER_ROLES.SUPERADMIN) {
    //   if (permissions && permissions.length > 0) {
    //     let permissionObj = {};
    //     permissions.map((item) => {
    //       return item.name.trim() === "Weekly Total"
    //         ? (permissionObj.weeklyTotal = true)
    //         : item.name.trim() === "Sales"
    //         ? (permissionObj.sales = true)
    //         : item.name.trim() === "Accounts"
    //         ? (permissionObj.accounts = true)
    //         : item.name.trim() === "Daily Sales States"
    //         ? (permissionObj.dailySalesStates = true)
    //         : item.name.trim() === "Consultations"
    //         ? (permissionObj.consultations = true)
    //         : item.name.trim() === "View"
    //         ? (permissionObj.view = true)
    //         : item.name.trim() === "Assign"
    //         ? (permissionObj.assign = true)
    //         : item.name.trim() === "UnAssign"
    //         ? (permissionObj.unAssign = true)
    //         : item.name.trim() === "Delete"
    //         ? (permissionObj.delete = true)
    //         : item.name.trim() === "Assigned Items"
    //         ? (permissionObj.assigned = true)
    //         : item.name.trim() === "Stocked Items"
    //         ? (permissionObj.stocked = true)
    //         : item.name.trim() === "Picked Up Items"
    //         ? (permissionObj.pickedUpItems = true)
    //         : item.name.trim() === "Transactions"
    //         ? (permissionObj.transactions = true)
    //         : item.name.trim() === "Cancelled Items"
    //         ? (permissionObj.cancelledItems = true)
    //         : null;
    //     });
    //     if (
    //       permissionObj.weeklyTotal &&
    //       permissionObj.sales &&
    //       permissionObj.accounts &&
    //       permissionObj.dailySalesStates &&
    //       permissionObj.consultations
    //     ) {
    //       this.setState({ Dashboard: true });
    //     } else {
    //       this.setState({ Dashboard: false });
    //     }
    //     if (
    //       permissionObj.view &&
    //       permissionObj.assign &&
    //       permissionObj.unAssign &&
    //       permissionObj.delete
    //     ) {
    //       this.setState({ assignprescription: true });
    //     } else {
    //       this.setState({ assignprescription: false });
    //     }
    //     let permissionUser = JSON.parse(
    //       sessionStorage.getItem("permissionUser")
    //     );
    //     if (permissionUser === USER_ROLES.DELIVERYTECH) {
    //       this.setState({ DeliveryTech: true });
    //     }
    //   } else {
    //     this.setState({ Dashboard: false });
    //     this.setState({ assignprescription: false });
    //     this.setState({ reports: false });
    //   }
    // }

    /** Report Permission Start Here */
    if (permissions && permissions.length > 0) {
      if (_checkUserRole === USER_ROLES.SUPERADMIN && !stockerGated) {
        this.setState({
          Dashboard: true,
          reports: true,
          assigneditemsreports: true,
          stockeditemsreports: true,
          pickedUpitemsreports: true,
          controlledSubstanceDispensedReport: true,
          transactionsreports: true,
          isIpayTransactionAllowed: true,
          isTemperatureMonitoringAllowed: true,
          isReturnToStockAllowed: true,
          isSurveyReportAllowed: true,
          isConsultationReportAllowed: true,
          isScheduleConsultationAllowed: true,
          isOutForDeliveryAllowed: true,
          isDeliveredItemsAllowed: true,
          isLoyaltiMembersAllowed: true,
          isOrderStagingViewAllowed: true,
          isConsoleReportAllowed: true,
          enrolledPatientsreports: true,
          payrollDeductionsreports: true,
          inActiveUsresAllowed: true,
          kioskUtilizationAllowed: true,
          isPhiLogsVisible: true,
          isPurgeTrailVisible: true,
          isCycleReview: true,
          cancelledItemsReports: true,
          isAuditTrailReportAllowed: true,
          isHL7OrdersViewVisible: true,
          isIpayReceivablesAllowed: true,
          isCashReceiptsAllowed: true,
          isDeliverPrescriptionVisible: true,
          isComplianceReconciliation: true,
          isMessageLogVisible: true,
        });
      } else {
        const assigneditemsreports = permissions.some((item) => item.name.trim() === "Assigned Items" && item.is_allowed === 1);
        const stockeditemsreports = permissions.some((item) => item.name.trim() === "Stocked Items" && item.is_allowed === 1);
        const pickedUpitemsreports = permissions.some((item) => item.name.trim() === "Picked Up Items" && item.is_allowed === 1);
        const controlledSubstanceDispensedReport = stockerGated
          ? permissions.some((item) => item.name.trim() === "Controlled Substance Dispensed" && item.is_allowed === 1)
          : (
            permissions.some((item) => item.name.trim() === "Controlled Substance Dispensed" && item.is_allowed === 1) ||
            permissions.some((item) => item.name.trim() === "Picked Up Items" && item.is_allowed === 1)
          );
        const transactionsreports = permissions.some((item) => item.name.trim() === "Transactions" && item.is_allowed === 1);
        const isIpayTransactionAllowed = permissions.some((item) => item.name.trim() === "iPay Transactions" && item.is_allowed === 1);
        const isTemperatureMonitoringAllowed = permissions.some((item) => item.name.trim() === "Temperature Monitoring" && item.is_allowed === 1);
        const isReturnToStockAllowed = permissions.some((item) => item.name.trim() === "Return to stock" && item.is_allowed === 1);
        const isSurveyReportAllowed = permissions.some((item) => item.name.trim() === "Survey Report" && item.is_allowed === 1);
        const isConsultationReportAllowed = permissions.some((item) => item.name.trim() === "Consultation Report" && item.is_allowed === 1);
        const isScheduleConsultationAllowed = permissions.some((item) => item.name.trim() === "Schedule Consultation" && item.is_allowed === 1);
        const isOutForDeliveryAllowed = permissions.some((item) => item.name.trim() === "Out for Delivery" && item.is_allowed === 1);
        const isDeliveredItemsAllowed = permissions.some((item) => item.name.trim() === "Delivered Items" && item.is_allowed === 1);
        const isLoyaltiMembersAllowed = permissions.some((item) => item.name.trim() === "Loyalty Members" && item.is_allowed === 1);
        const isOrderStagingViewAllowed = permissions.some((item) => item.name.trim() === "Order Staging View" && item.is_allowed === 1);
        const isConsoleReportAllowed = permissions.some((item) => item.name.trim() === "Console Report" && item.is_allowed === 1);
        const enrolledPatientsreports = permissions.some((item) => item.name.trim() === "Patient List" && item.is_allowed === 1);
        const payrollDeductionsreports = permissions.some((item) => item.name.trim() === "Payroll Deduction" && item.is_allowed === 1);
        const inActiveUsresAllowed = permissions.some((item) => item.name.trim() === "InActive Users" && item.is_allowed === 1);
        const kioskUtilizationAllowed = permissions.some((item) => item.name.trim() === "Kiosk Utilization" && item.is_allowed === 1);
        const isPhiLogsVisible = permissions.some((item) => item.name.trim() === "PMS Logs" && item.is_allowed === 1);
        const isPurgeTrailVisible = permissions.some((item) => item.name.trim() === "Purge trail" && item.is_allowed === 1);
        const isCycleReview = permissions.some((item) => item.name.trim() === "Cycle Review" && item.is_allowed === 1);
        const cancelledItemsReports = permissions.some((item) => item.name.trim() === "Cancelled Items" && item.is_allowed === 1);
        const isAuditTrailReportAllowed = permissions.some((item) => item.name.trim() === "Audit trail" && item.is_allowed === 1);
        const isHL7OrdersViewVisible = permissions.some((item) => item.name.trim() === "HL7 Order Staging" && item.is_allowed === 1);
        const isIpayReceivablesAllowed = permissions.some((item) => item.name.trim() === "iPay Receivables" && item.is_allowed === 1);
        const isDeliverPrescriptionVisible = permissions.some((item) => item.name.trim() === "Assign" && item.is_allowed === 1);
        const isComplianceReconciliation = permissions.some((item) => item.name.trim() === "Compliance Reconciliation" && item.is_allowed === 1);
        const isMessageLogVisible = stockerGated
          ? permissions.some((item) => item.name.trim() === "Message Log" && item.is_allowed === 1)
          : true;
        const isCashReceiptsAllowed = permissions.some((item) => item.name.trim() === "Cash Receipts" && item.is_allowed === 1);
        const reports = stockerGated
          ? (
            assigneditemsreports ||
            stockeditemsreports ||
            pickedUpitemsreports ||
            controlledSubstanceDispensedReport ||
            transactionsreports ||
            isIpayTransactionAllowed ||
            isTemperatureMonitoringAllowed ||
            isReturnToStockAllowed ||
            isSurveyReportAllowed ||
            isConsultationReportAllowed ||
            isScheduleConsultationAllowed ||
            isOutForDeliveryAllowed ||
            isDeliveredItemsAllowed ||
            isLoyaltiMembersAllowed ||
            isOrderStagingViewAllowed ||
            isConsoleReportAllowed ||
            enrolledPatientsreports ||
            payrollDeductionsreports ||
            inActiveUsresAllowed ||
            kioskUtilizationAllowed ||
            isPhiLogsVisible ||
            isPurgeTrailVisible ||
            isCycleReview ||
            cancelledItemsReports ||
            isAuditTrailReportAllowed ||
            isHL7OrdersViewVisible ||
            isIpayReceivablesAllowed ||
            isComplianceReconciliation ||
            isMessageLogVisible ||
            isCashReceiptsAllowed
          )
          : true;
        this.setState({
          Dashboard: stockerGated ? hasAllowedPermission({ entityType: "Dashboard" }) : true,
          reports,
          assigneditemsreports,
          stockeditemsreports,
          pickedUpitemsreports,
          controlledSubstanceDispensedReport,
          transactionsreports,
          isIpayTransactionAllowed,
          isTemperatureMonitoringAllowed,
          isReturnToStockAllowed,
          isSurveyReportAllowed,
          isConsultationReportAllowed,
          isScheduleConsultationAllowed,
          isOutForDeliveryAllowed,
          isDeliveredItemsAllowed,
          isLoyaltiMembersAllowed,
          isOrderStagingViewAllowed,
          isConsoleReportAllowed,
          enrolledPatientsreports,
          payrollDeductionsreports,
          inActiveUsresAllowed,
          kioskUtilizationAllowed,
          isPhiLogsVisible,
          isPurgeTrailVisible,
          isCycleReview,
          cancelledItemsReports,
          isAuditTrailReportAllowed,
          isHL7OrdersViewVisible,
          isIpayReceivablesAllowed,
          isCashReceiptsAllowed,
          isDeliverPrescriptionVisible,
          isComplianceReconciliation,
          isMessageLogVisible,
        });

      }
    } else {
      this.setState({
        Dashboard: false,
        assigneditemsreports: false,
        stockeditemsreports: false,
        pickedUpitemsreports: false,
        controlledSubstanceDispensedReport: false,
        transactionsreports: false,
        isIpayTransactionAllowed: false,
        isTemperatureMonitoringAllowed: false,
        isReturnToStockAllowed: false,
        isSurveyReportAllowed: false,
        isConsultationReportAllowed: false,
        isScheduleConsultationAllowed: false,
        isOutForDeliveryAllowed: false,
        isDeliveredItemsAllowed: false,
        isLoyaltiMembersAllowed: false,
        isOrderStagingViewAllowed: false,
        isConsoleReportAllowed: false,
        enrolledPatientsreports: false,
        payrollDeductionsreports: false,
        inActiveUsresAllowed: false,
        kioskUtilizationAllowed: false,
        isPhiLogsVisible: false,
        isPurgeTrailVisible: false,
        isCycleReview: false,
        cancelledItemsReports: false,
        isAuditTrailReportAllowed: false,
        isHL7OrdersViewVisible: false,
        isIpayReceivablesAllowed: false,
        isCashReceiptsAllowed: false,
        isDeliverPrescriptionVisible: false,
        isComplianceReconciliation: false,
        isMessageLogVisible: false,
        reports: false,
      });
    }
    this.syncReportsOpenFromRoute();
  }
  componentWillUnmount() {
    if (this._navAccordionUnsub) {
      this._navAccordionUnsub();
    }
  }
  render() {
    const { classes, t } = this.props;
    const { isDeliverPrescriptionVisible } = this.state;
    const { reportsOnly, hideReportsMenu, nested, marketplaceReportsOnly, sectionLinksOnly } =
      this.props;
    const showDashboard =
      this.state.Dashboard === true && !this.state.DeliveryTech;
    const showDeliverPrescription = isDeliverPrescriptionVisible;
    const showReports =
      !hideReportsMenu &&
      this.state.reports === true &&
      !this.state.DeliveryTech;
    const _isRetail = localStorage.getItem("isRetail");
    const showQueue =
      !isNewUiHidden() &&
      showDashboard &&
      !this.state.DeliveryTech &&
      [
        false,
        "false",
        undefined,
        "undefined",
        null,
        "null",
        "",
      ].includes(_isRetail);
    const isNestedReports = nested || reportsOnly;
    if (sectionLinksOnly && marketplaceReportsOnly) {
      return (
        <Location>
          {({ location }) => (
            <div className={classes.nestedReportsMenu}>
              {this.renderMarketplaceReportLinks(location, true)}
            </div>
          )}
        </Location>
      );
    }
    return (
      <Location>
        {({ location }) => {
          const onReports = marketplaceReportsOnly
            ? isMarketplaceReportPath(location.pathname)
            : isGeneralReportPath(location.pathname);
          this._currentOnReports = onReports;
          const reportsOpen = reportsOnly
            ? this.props.accordionSectionOpen === true
            : isTopLevelNavSectionOpen(TOP_LEVEL_NAV.REPORTS);
          if (reportsOnly) {
            return this.state.reports === true && !this.state.DeliveryTech ? (
              <div className={classes.root}>
                {this.renderReportsMenu(
                  location,
                  onReports,
                  reportsOpen,
                  true,
                  marketplaceReportsOnly
                )}
              </div>
            ) : null;
          }
          return (
      <div id="react-no-print" className={classes.root}>
        {showDashboard ? this.wrapNavTitle(
          t("Leftbar.Dashboard"),
          <Link
            to={
              [
                false,
                "false",
                undefined,
                "undefined",
                null,
                "null",
                "",
              ].includes(_isRetail)
                ? "/home/"
                : "/home/dashboard"
            }
            className={classes.navLink}
          >
            <ListItemButton
              className={`${classes.listItem} ${classes.primaryNavItem}`}
              selected={
                isNavPathActive(location.pathname, "/home/") ||
                isNavPathActive(location.pathname, "/home") ||
                isNavPathActive(location.pathname, "/home/dashboard")
              }
            >
              <ListItemIcon>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText
                primary={t("Leftbar.Dashboard")}
                className={classes.textColor}
              />
            </ListItemButton>
          </Link>
        ) : (
          ""
        )}

        {this.renderDeliverPrescriptionNav(location)}

        {showQueue ? (
          <Divider className={classes.primaryNavDivider} />
        ) : null}

        {showQueue ? (
          <QueueNavItems
            location={location}
            drawerOpen={this.props.drawerOpen}
            wrapNavTitle={this.wrapNavTitle}
            classes={classes}
          />
        ) : null}

        {/* {isDeliverPrescriptionVisible ? (
          <React.Fragment>
            <Divider />
            {[
              false,
              "false",
              undefined,
              "undefined",
              null,
              "null",
              "",
            ].includes(_isRetail) ? (
              <div>
                {this.state.assignprescription === true &&
                !this.state.DeliveryTech ? (
                  this.props.isVisible === true ? (
                    <Link
                      to="/home/prescription/unpluggedassign"
                      className={classes.navLink}
                    >
                      <ListItemButton className={classes.listItem}>
                        <ListItemIcon>
                          <AssignPrescriptionIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={t("Leftbar.DeliverPrescription")}
                          className={classes.textColor}
                        />
                      </ListItemButton>
                    </Link>
                  ) : (
                    <Link
                      to="/home/prescription/assign"
                      className={classes.navLink}
                    >
                      <ListItemButton className={classes.listItem}>
                        <ListItemIcon>
                          <AssignPrescriptionIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={t("Leftbar.DeliverPrescription")}
                          className={classes.textColor}
                        />
                      </ListItemButton>
                    </Link>
                  )
                ) : (
                  ""
                )}
              </div>
            ) : (
              <Link
                to={
                  [
                    false,
                    "false",
                    undefined,
                    "undefined",
                    null,
                    "null",
                    "",
                  ].includes(_isRetail)
                    ? "/home/create-orders"
                    : "/home/"
                }
                className={classes.navLink}
              >
                <ListItemButton className={classes.listItem}>
                  <ListItemIcon>
                    <AssignPrescriptionIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("Leftbar.CreateLockerPickup")}
                    className={classes.textColor}
                  />
                </ListItemButton>
              </Link>
            )}
          </React.Fragment>
        ) : null} */}
        {(showQueue || showDeliverPrescription) && showReports ? (
          <Divider className={classes.primaryNavDivider} />
        ) : null}
        {showReports
          ? this.renderReportsMenu(
              location,
              onReports,
              reportsOpen,
              isNestedReports,
              false
            )
          : null}
      </div>
          );
        }}
      </Location>
    );
  }

  renderMarketplaceReportLinks = (location, nested) => {
    const { t } = this.props;
    if (localStorage.getItem("isIlocalUser") !== "true") {
      return null;
    }
    const items = [
      {
        to: "/home/reports/feature-purchases",
        labelKey: "Leftbar.FeaturePurchasesReport",
        icon: ShoppingBagIcon,
      },
      {
        to: "/home/reports/feature-requests",
        labelKey: "Leftbar.FeatureRequestsReport",
        icon: PendingActionsIcon,
      },
      {
        to: "/home/reports/feature-enablement-history",
        labelKey: "Leftbar.EnablementHistoryReport",
        icon: HistoryIcon,
      },
    ];
    return (
      <React.Fragment>
        {items.map((item) =>
          this.renderReportNavLink(
            item.to,
            t(item.labelKey),
            location,
            nested,
            item.to,
            item.icon
          )
        )}
      </React.Fragment>
    );
  };

  renderReportsMenu = (
    location,
    onReports,
    reportsOpen,
    nested,
    marketplaceReportsOnly
  ) => {
    const { classes, t, reportsOnly } = this.props;
    const onReportsMenuToggle = reportsOnly
      ? this.props.onAccordionSectionToggle
      : this.handleReportsMenuToggle;
    if (marketplaceReportsOnly) {
      if (localStorage.getItem("isIlocalUser") !== "true") {
        return null;
      }
      const licensingLabel = t("Leftbar.LicensingReports", {
        defaultValue: "Licensing Reports",
      });
      if (!this.props.drawerOpen) {
        const licensingOpen = this.getCategoryOpen("licensing");
        const licensingActive = isMarketplaceReportPath(location.pathname);
        return (
          <React.Fragment>
            <ListItemButton
              onClick={() => this.handleCategoryClick("licensing")}
              className={
                `${classes.listItem} ${classes.reportCategoryToggle} ` +
                "report-cat-ribbon report-cat-licensing nav-flyout-section-toggle"
              }
              selected={licensingActive}
            >
              {this.renderNavIcon(AssessmentIcon)}
              <ListItemText
                primary={licensingLabel}
                className={`${classes.reportCategoryLabel} ${classes.reportCategoryRibbonLabel}`}
                style={{ paddingLeft: "0px" }}
              />
              {licensingOpen ? (
                <ExpandLess className="nav-section-chevron" />
              ) : (
                <ExpandMore className="nav-section-chevron" />
              )}
            </ListItemButton>
            <Collapse in={licensingOpen} timeout="auto" unmountOnExit>
              <div className="nav-flyout-section-menu">
                {this.renderMarketplaceReportLinks(location, false)}
              </div>
            </Collapse>
          </React.Fragment>
        );
      }
      return (
        <NavMiniFlyout
          drawerOpen={this.props.drawerOpen}
          menuOpen={reportsOpen}
          onMenuToggle={onReportsMenuToggle}
          selected={onReports}
          buttonClassName={
            classes.listItem +
            ` ${classes.nestedReportsToggle} report-cat-ribbon report-cat-licensing`
          }
          icon={this.renderNavIcon(AssessmentIcon)}
          label={licensingLabel}
          labelClassName={classes.reportCategoryRibbonLabel}
          labelStyle={{ paddingLeft: "0px" }}
          flyoutMinWidth={280}
        >
          <div className={classes.nestedReportsMenu}>
            {this.renderMarketplaceReportLinks(location, nested)}
          </div>
        </NavMiniFlyout>
      );
    }
    const flyoutMode = !this.props.drawerOpen && !nested;
    return (
          <NavMiniFlyout
            drawerOpen={this.props.drawerOpen}
            menuOpen={reportsOpen}
            onMenuToggle={onReportsMenuToggle}
            selected={onReports}
            buttonClassName={
              classes.listItem +
              (nested
                ? ` ${classes.nestedReportsToggle}`
                : ` ${classes.primaryNavItem}`)
            }
            icon={
              !nested ? (
                <ListItemIcon>
                  <ReportsIcon />
                </ListItemIcon>
              ) : null
            }
            label={t("Leftbar.Report")}
            labelClassName={classes.textColor}
            labelStyle={{ paddingLeft: nested ? "16px" : "0px" }}
            flyoutMinWidth={320}
          >
            <div className={nested ? classes.nestedReportsMenu : undefined}>
              {this.renderCategorizedReports(
                location,
                nested,
                this.state,
                flyoutMode
              )}
            </div>
          </NavMiniFlyout>
    );
  }
}

NestedList.propTypes = {
  classes: PropTypes.object.isRequired,
  isVisible: PropTypes.bool,
  reportsOnly: PropTypes.bool,
  marketplaceReportsOnly: PropTypes.bool,
  hideReportsMenu: PropTypes.bool,
  nested: PropTypes.bool,
  drawerOpen: PropTypes.bool,
  sectionLinksOnly: PropTypes.bool,
  accordionSectionOpen: PropTypes.bool,
  onAccordionSectionToggle: PropTypes.func,
};

NestedList.defaultProps = {
  isVisible: false,
  reportsOnly: false,
  marketplaceReportsOnly: false,
  hideReportsMenu: false,
  nested: false,
  drawerOpen: true,
  sectionLinksOnly: false,
  accordionSectionOpen: false,
};

export default withStyles(styles)(withTranslation()(NestedList));



// WEBPACK FOOTER //
// ./src/components/leftnav/reportlink.js